import { T } from '@tolgee/react';
import { ReactNode } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import starsSticker from '../../../assets/svg/stickers/stars.svg';

const GlobalKeyframes = createGlobalStyle`
  @keyframes strengthLoaderSpin {
    to { transform: rotate(360deg); }
  }
  @keyframes strengthLoaderPulseOrb {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.85; }
  }
  @keyframes strengthLoaderGradientShift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  @keyframes strengthLoaderEllipDots {
    0%   { content: ''; }
    25%  { content: '.'; }
    50%  { content: '..'; }
    75%  { content: '...'; }
    100% { content: ''; }
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  text-align: center;
  padding: 20px;
`;

const OrbStage = styled.div`
  width: 160px;
  height: 160px;
  margin: 0 auto 32px;
  position: relative;
`;

const OrbitOuter = styled.div`
  position: absolute;
  inset: 0;
  animation: strengthLoaderSpin 8s linear infinite;
`;

const OrbitOuterStar = styled.img`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 36px;
  height: 36px;
`;

const OrbitInner = styled.div`
  position: absolute;
  inset: 0;
  animation: strengthLoaderSpin 6s linear infinite reverse;
`;

const OrbitInnerStar = styled.img`
  position: absolute;
  bottom: 8px;
  right: 10px;
  width: 22px;
  height: 22px;
  opacity: 0.7;
`;

const Orb = styled.div`
  position: absolute;
  inset: 24px;
  border-radius: 50%;
  background: linear-gradient(90deg, #ffa55b 59.9%, #ffc107 100%);
  background-size: 200% 200%;
  animation: strengthLoaderGradientShift 6s ease-in-out infinite,
    strengthLoaderPulseOrb 2.4s ease-in-out infinite;
  box-shadow: 0 0 40px rgba(255, 165, 91, 0.5), 0 0 80px rgba(255, 193, 7, 0.25);
`;

const OrbHighlight = styled.div`
  position: absolute;
  inset: 38px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(255, 255, 255, 0.7),
    transparent 60%
  );
  animation: strengthLoaderPulseOrb 2.4s ease-in-out infinite;
  pointer-events: none;
`;

const Headline = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 6px;
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.55);
  &::after {
    content: '';
    display: inline-block;
    width: 1.2em;
    text-align: left;
    animation: strengthLoaderEllipDots 1.4s steps(4, end) infinite;
  }
`;

interface StrengthGeneratingLoaderProps {
  headline?: ReactNode;
  subtitle?: ReactNode;
}

const StrengthGeneratingLoader = ({
  headline,
  subtitle,
}: StrengthGeneratingLoaderProps = {}) => (
  <>
    <GlobalKeyframes />
    <Wrapper>
      <OrbStage aria-hidden="true">
        <OrbitOuter>
          <OrbitOuterStar src={starsSticker.src} alt="" />
        </OrbitOuter>
        <OrbitInner>
          <OrbitInnerStar src={starsSticker.src} alt="" />
        </OrbitInner>
        <Orb />
        <OrbHighlight />
      </OrbStage>
      <Headline>
        {headline ?? (
          <T keyName="generatingStrength" defaultValue="Generating Strength" />
        )}
      </Headline>
      <Subtitle>
        {subtitle ?? (
          <T
            keyName="readingResponseCarefully"
            defaultValue="Reading the response carefully"
          />
        )}
      </Subtitle>
    </Wrapper>
  </>
);

export default StrengthGeneratingLoader;
